import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  const refreshMe = useCallback(() =>
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecked(true)), []);

  // No automatic login on a fresh visit (new tab / browser): leftover cookies are cleared.
  // Within the same tab a refresh keeps the session (sessionStorage marker only, no credentials stored).
  useEffect(() => {
    if (sessionStorage.getItem("tab_session") === "1") { refreshMe(); return; }
    api.post("/auth/logout").catch(() => null).finally(() => { setUser(false); setChecked(true); });
  }, [refreshMe]);

  const login = async (email, password) => {
    try {
      await api.post("/auth/login", { email, password });
      sessionStorage.setItem("tab_session", "1");
      await refreshMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    sessionStorage.removeItem("tab_session");
    await api.post("/auth/logout").catch(() => null);
    setUser(false);
  };

  const can = (perm) => !!user && (user.permissions?.includes("*") || user.permissions?.includes(perm));

  return (
    <AuthContext.Provider value={{ user, checked, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
